import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import { Grid, Box, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import {
  WsTypography,
  WsTypoVariants,
  WsElementWithPopOver,
  WsIconMoreVer,
  WsActiveIconAndText,
  WsDisableIconAndText,
  WsIconText,
  WsButton,
  WsColors,
  WsTypoColors,
} from 'wisestep-base';

import { PromptDialog } from '../promptDialog';

interface PromptHeaderProps {
  name: string;
}

export const PromptHeader = ({ name }: PromptHeaderProps) => {
  const { control, getValues, watch } = useFormContext();
  const [editIndex, setEditIndex] = useState<number>(-1);
  const { fields, append, remove, update } = useFieldArray({
    control,
    name,
  });

  const addNewPrompt = (data) => {
    editIndex != -1 ? update(editIndex, data) : append(data);
    setEditIndex(-1);
    setOpenDialog(false);
  };

  const activatePrompt = (index: number) =>
    fields.map((field, idx) => {
      update(idx, { ...field, isActive: idx === index });
    });
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [viewMore, setViewMore] = useState<boolean>(false);
  const aiReplyEnabled = watch('aiReplyEnabled');
  useEffect(() => {
    if (aiReplyEnabled) {
      setViewMore(false);
    }
  }, [aiReplyEnabled]);
  return (
    <Grid xs={12} display="flex" flexDirection="column" rowGap={1}>
      {fields.length == 0 && (
        <Grid>
          <WsTypography>No prompt added</WsTypography>{' '}
        </Grid>
      )}
      {(viewMore || aiReplyEnabled) &&
        fields.map((field, index) => {
          const data = getValues(`${name}.${index}`);

          return (
            <Box key={index}>
              <Grid container alignItems="center" spacing={1}>
                <Grid item xs={3}>
                  <WsTypography variant={WsTypoVariants.BODY1} bold={true}>
                    {data?.promptTitle}
                  </WsTypography>
                </Grid>
                <Grid item xs={9} container justifyContent="flex-end" alignItems="center">
                  {data?.isActive ? <WsActiveIconAndText /> : <WsDisableIconAndText />}
                  <WsElementWithPopOver element={<WsIconMoreVer />}>
                    <Box display="flex" flexDirection="column" gap={1} px={3} py={1}>
                      {!data?.isActive && (
                        <WsIconText
                          onClick={() => activatePrompt(index)}
                          icon={
                            <CheckCircleOutlinedIcon
                              fontSize="small"
                              sx={{ color: WsColors.TertiaryShades900 }}
                            />
                          }
                          text="Activate Prompt"
                          textCursor="pointer"
                        />
                      )}
                      <WsIconText
                        onClick={() => {
                          setEditIndex(index);
                          setOpenDialog(true);
                        }}
                        icon={
                          <ModeEditOutlineOutlinedIcon
                            fontSize="small"
                            sx={{ color: WsColors.TertiaryShades900 }}
                          />
                        }
                        text="Edit Prompt"
                        textCursor="pointer"
                      />
                      <WsIconText
                        onClick={() => remove(index)}
                        icon={
                          <DeleteOutlineOutlinedIcon
                            fontSize="small"
                            sx={{ color: WsColors.TertiaryShades900 }}
                          />
                        }
                        text="Delete Prompt"
                        textCursor="pointer"
                      />
                    </Box>
                  </WsElementWithPopOver>
                </Grid>
              </Grid>
              <Grid container>
                <TextField
                  type="text"
                  value={data?.prompt}
                  disabled
                  multiline
                  minRows={6}
                  size="small"
                  sx={{
                    width: '60%',
                    backgroundColor: WsColors.TertiaryShades100,
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: '#11151C',
                    },
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            </Box>
          );
        })}

      {fields.length > 0 && !viewMore && !aiReplyEnabled && (
        <Grid>
          <WsTypography
            color={WsTypoColors.PRIMARY600}
            cursor="pointer"
            onClick={() => setViewMore(true)}
          >{`View ${fields.length} Prompts`}</WsTypography>
        </Grid>
      )}

      <Grid>
        <WsButton startIcon={<AddIcon />} onClick={() => setOpenDialog(true)} size="small">
          Add Prompt
        </WsButton>
      </Grid>
      {openDialog && (
        <PromptDialog
          defaultValues={
            editIndex != -1 ? fields[editIndex] : { promptTitle: '', prompt: '', isActive: false }
          }
          openPromptDialog={openDialog}
          handleOpenPromptDialogForEdit={() => {
            setEditIndex(-1);
            setOpenDialog(false);
          }}
          onSave={addNewPrompt}
        />
      )}
    </Grid>
  );
};
